class TurkeyCalculatorModal extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.totalSteps = 4;
    this.data = {
      guests: 6,
      hunger: 'average',
      leftovers: true
    };

    // Hunger level modifiers (adjusts effective guest count)
    this.hungerModifier = {
      light: -1,    // Light eaters = 1 fewer "effective" guest
      average: 0,   // No adjustment
      hearty: 3     // Hearty appetites = 3 extra "effective" guests
    };

    // Leftovers adds equivalent of extra guests
    this.leftoversExtra = 4;

    // Size mappings based on serving ranges
    // Using upper bound of serving range for each size
    this.sizeOptions = [
      { value: '5-6kg (Serves 6-8)', maxGuests: 8, label: '5-6kg' },
      { value: '6-7kg (Serves 8-10)', maxGuests: 10, label: '6-7kg' },
      { value: '7-8kg (Serves 10-12)', maxGuests: 12, label: '7-8kg' },
      { value: '8-9kg (Serves 12-14)', maxGuests: 14, label: '8-9kg' },
      { value: '9-10kg (Serves 14-16)', maxGuests: 16, label: '9-10kg' },
      { value: '10kg+', maxGuests: Infinity, label: '10kg+' }
    ];
  }

  connectedCallback() {
    this.setupEventListeners();
    this.updateStepVisibility();
    this.updateProgressBar();
  }

  setupEventListeners() {
    // Close button
    const closeBtn = this.querySelector('[data-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hide());
    }

    // Click outside to close
    this.addEventListener('click', (event) => {
      if (event.target === this) this.hide();
    });

    // ESC key to close
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    // Navigation buttons
    this.querySelectorAll('[data-next]').forEach(btn => {
      btn.addEventListener('click', () => this.nextStep());
    });

    this.querySelectorAll('[data-prev]').forEach(btn => {
      btn.addEventListener('click', () => this.prevStep());
    });

    // Restart button
    const restartBtn = this.querySelector('[data-restart]');
    if (restartBtn) {
      restartBtn.addEventListener('click', () => this.restart());
    }

    // Guest input
    const guestInput = this.querySelector('[data-guest-input]');
    if (guestInput) {
      guestInput.addEventListener('input', (e) => {
        this.data.guests = parseInt(e.target.value) || 1;
        this.updateGuestDisplay();
      });
    }

    // Guest increment/decrement buttons
    const decrementBtn = this.querySelector('[data-guest-decrement]');
    const incrementBtn = this.querySelector('[data-guest-increment]');

    if (decrementBtn) {
      decrementBtn.addEventListener('click', () => {
        if (this.data.guests > 1) {
          this.data.guests--;
          this.updateGuestInput();
        }
      });
    }

    if (incrementBtn) {
      incrementBtn.addEventListener('click', () => {
        if (this.data.guests < 30) {
          this.data.guests++;
          this.updateGuestInput();
        }
      });
    }

    // Hunger options
    this.querySelectorAll('[data-hunger]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.data.hunger = e.currentTarget.dataset.hunger;
        this.updateHungerSelection();
      });
    });

    // Leftovers options
    this.querySelectorAll('[data-leftovers]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.data.leftovers = e.currentTarget.dataset.leftovers === 'true';
        this.updateLeftoversSelection();
      });
    });

    // Select size button
    const selectSizeBtn = this.querySelector('[data-select-size]');
    if (selectSizeBtn) {
      selectSizeBtn.addEventListener('click', () => this.selectRecommendedSize());
    }
  }

  updateGuestInput() {
    const input = this.querySelector('[data-guest-input]');
    if (input) {
      input.value = this.data.guests;
    }
    this.updateGuestDisplay();
  }

  updateGuestDisplay() {
    const display = this.querySelector('[data-guest-count]');
    if (display) {
      display.textContent = this.data.guests;
    }
  }

  updateHungerSelection() {
    this.querySelectorAll('[data-hunger]').forEach(btn => {
      btn.classList.toggle('selected', btn.dataset.hunger === this.data.hunger);
    });
  }

  updateLeftoversSelection() {
    this.querySelectorAll('[data-leftovers]').forEach(btn => {
      const isSelected = (btn.dataset.leftovers === 'true') === this.data.leftovers;
      btn.classList.toggle('selected', isSelected);
    });
  }

  calculateTurkeySize() {
    // Calculate effective guest count based on hunger and leftovers
    let effectiveGuests = this.data.guests;
    effectiveGuests += this.hungerModifier[this.data.hunger];
    if (this.data.leftovers) {
      effectiveGuests += this.leftoversExtra;
    }

    // Ensure minimum of 1 effective guest
    effectiveGuests = Math.max(1, effectiveGuests);

    // Find the appropriate size based on effective guest count
    for (const size of this.sizeOptions) {
      if (effectiveGuests <= size.maxGuests) {
        return {
          effectiveGuests: effectiveGuests,
          size: size.value,
          label: size.label
        };
      }
    }

    // Default to largest
    return {
      effectiveGuests: effectiveGuests,
      size: '10kg+',
      label: '10kg+'
    };
  }

  updateResultDisplay() {
    const result = this.calculateTurkeySize();

    const sizeDisplay = this.querySelector('[data-result-size]');
    const guestsDisplay = this.querySelector('[data-result-guests]');
    const hungerDisplay = this.querySelector('[data-result-hunger]');

    if (sizeDisplay) sizeDisplay.textContent = result.label;
    if (guestsDisplay) guestsDisplay.textContent = this.data.guests;
    if (hungerDisplay) hungerDisplay.textContent = this.data.hunger;

    // Update leftovers text visibility
    const leftoversText = this.querySelector('.turkey-calc__result-leftovers');
    if (leftoversText) {
      leftoversText.style.display = this.data.leftovers ? 'inline' : 'none';
    }

    // Store the recommended size for selection
    this.recommendedSize = result.size;
  }

  selectRecommendedSize() {
    // Find the variant selector on the page
    const variantSelect = document.querySelector('select[name="options[Weight]"]');

    if (variantSelect && this.recommendedSize) {
      // Find and select the matching option
      const options = variantSelect.options;
      for (let i = 0; i < options.length; i++) {
        if (options[i].value === this.recommendedSize) {
          variantSelect.selectedIndex = i;
          // Trigger change event to update the variant
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    }

    this.hide();
  }

  nextStep() {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.updateStepVisibility();
      this.updateProgressBar();

      // Update result on final step
      if (this.currentStep === this.totalSteps) {
        this.updateResultDisplay();
      }
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.updateStepVisibility();
      this.updateProgressBar();
    }
  }

  restart() {
    this.currentStep = 1;
    this.data = {
      guests: 6,
      hunger: 'average',
      leftovers: true
    };
    this.updateGuestInput();
    this.updateHungerSelection();
    this.updateLeftoversSelection();
    this.updateStepVisibility();
    this.updateProgressBar();
  }

  updateStepVisibility() {
    this.querySelectorAll('[data-step]').forEach(step => {
      const stepNum = parseInt(step.dataset.step);
      step.classList.toggle('active', stepNum === this.currentStep);
    });
  }

  updateProgressBar() {
    const progressFill = this.querySelector('[data-progress-fill]');
    const progressSteps = this.querySelectorAll('[data-progress-step]');

    if (progressFill) {
      const progress = (this.currentStep / this.totalSteps) * 100;
      progressFill.style.width = `${progress}%`;
    }

    progressSteps.forEach(step => {
      const stepNum = parseInt(step.dataset.progressStep);
      step.classList.toggle('completed', stepNum < this.currentStep);
      step.classList.toggle('active', stepNum === this.currentStep);
    });
  }

  show(opener) {
    this.openedBy = opener;
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    this.restart(); // Reset to step 1 when opening

    // Focus trap
    const dialog = this.querySelector('[role="dialog"]');
    if (dialog && typeof trapFocus === 'function') {
      trapFocus(this, dialog);
    }
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    this.removeAttribute('open');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.openedBy);
    }
  }
}

customElements.define('turkey-calculator-modal', TurkeyCalculatorModal);

// Modal opener for the turkey calculator
class TurkeyCalculatorOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector('button');
    if (button) {
      button.addEventListener('click', () => {
        const modal = document.querySelector('turkey-calculator-modal');
        if (modal) modal.show(button);
      });
    }
  }
}

customElements.define('turkey-calculator-opener', TurkeyCalculatorOpener);
